/**
 * Extract a simplified line-structuring view from a single page's raw_data.
 * Returns an array of strings (one per text_line) by concatenating the text of
 * all descendant text_unit nodes.  Falls back to the original object when the
 * hierarchical format is not recognised.
 */
export const extractLineStructuring = (rawData: any): string[] | any => {
  if (!rawData?.root || !rawData?.objects) return rawData;

  const objects: Record<string, any> = rawData.objects;

  const getTextForNode = (uuid: string): string => {
    const node = objects[uuid];
    if (!node) return "";
    if (node.kind === "text_unit" || node.kind === "text_unit_preview") {
      return node.text ?? "";
    }
    if (Array.isArray(node.children)) {
      return node.children.map(getTextForNode).filter(Boolean).join(" ");
    }
    return "";
  };

  const rootNode =
    typeof rawData.root === "string"
      ? rawData.objects[rawData.root]
      : rawData.root;
  const rootChildren: string[] = rootNode?.children ?? [];
  return rootChildren
    .map((uuid: string) => {
      const node = objects[uuid];
      if (node?.kind === "text_line") return getTextForNode(uuid);
      return null;
    })
    .filter((l): l is string => l !== null && l.trim() !== "");
};

// Normalize graph from either old nested {chartok_coords, edges} or new flat {symbols, coords, edges}
const normalizeGraph = (
  g: any,
): { symbols: string[]; coords: number[][]; edges: number[][] } | null => {
  if (!g) return null;
  if (g.chartok_coords) {
    return {
      symbols: g.chartok_coords.symbols || [],
      coords: g.chartok_coords.coords || [],
      edges: g.edges || [],
    };
  }
  if (g.symbols) return g;
  return null;
};

// Transform new hierarchical JSON format to legacy flat objects array format
// For ImageDisplay: excludes text_unit_preview (shows original text_units and other objects)
export const transformToLegacyFormat = (data: any): any => {
  // If data already has objects array, return as-is (legacy format)
  if (data?.objects && Array.isArray(data.objects)) {
    console.log("transformToLegacyFormat: Already in legacy format");
    return data;
  }

  // If data has root property (new format), transform it
  if (data?.root && data?.objects) {
    const objects: any[] = [];

    // Iterate through all nodes in data.objects
    Object.keys(data.objects).forEach((nodeId) => {
      const node = data.objects[nodeId];

      // Skip nodes without kind
      if (!node?.kind) return;

      // Map "kind" to "cls" and extract relevant fields
      // EXCLUDE text_unit_preview from ImageDisplay
      if (node.bbox && node.kind !== "text_unit_preview") {
        const cls = node.kind.replace("_preview", ""); // e.g., "molecule_preview" -> "molecule"
        const metadata: any = {};

        const isMolecule =
          node.kind === "molecule" || node.kind === "molecule_preview";
        if (isMolecule) {
          // Molecules store SMILES in node.smiles (separate field from node.text)
          if (node.smiles !== undefined && node.smiles !== null) {
            metadata.smiles = node.smiles;
          } else if (node.text !== undefined && node.text !== null) {
            metadata.smiles = node.text;
          }
          // Extract graph for DRKit rendering
          if (node.graph) {
            const normalized = normalizeGraph(node.graph);
            if (normalized) metadata.graph = normalized;
          }
        } else if (node.text !== undefined && node.text !== null) {
          metadata.text = node.text;
        }

        // Preserve block_kind so the image-overlay can distinguish block
        // subtypes (e.g. red border for vertical_text).
        if (node.metadata?.block_kind) {
          metadata.block_kind = node.metadata.block_kind;
        }

        objects.push({
          cls,
          bbox: node.bbox,
          metadata,
          uuid: nodeId, // Keep UUID for hover mapping
        });
      }
    });

    console.log(
      "transformToLegacyFormat: Transformed",
      objects.length,
      "objects",
    );
    return { objects };
  }

  // Return original data if format is unrecognized
  console.log("transformToLegacyFormat: Unrecognized format", data);
  return data;
};

// Transform for StructuredJSONDisplay: extracts text_lines with clusters and includes molecules
export const transformToPositionedFormat = (data: any): any => {
  // Legacy flat format: { objects: [...] }
  // Convert to positioned format so StructuredJSONDisplay can render it.
  if (data?.objects && Array.isArray(data.objects)) {
    console.log("transformToPositionedFormat: Converting legacy flat format");

    const textUnitsRaw: any[] = data.objects.filter(
      (o: any) => o.cls === "text_unit" && o.bbox && o.metadata?.text,
    );
    const moleculesRaw: any[] = data.objects.filter(
      (o: any) => (o.cls === "molecule" || o.cls === "rxn") && o.bbox,
    );

    // Dump graph structure to find SMILES location
    if (moleculesRaw.length > 0) {
      const m = moleculesRaw[0];
      console.log(
        "[transformToPositionedFormat] first molecule metadata.graph:",
        JSON.stringify(m.metadata?.graph),
      );
    }
    const tablesRaw: any[] = data.objects.filter(
      (o: any) => o.cls === "table" && o.bbox,
    );

    // Sort text units top-to-bottom, left-to-right
    textUnitsRaw.sort((a: any, b: any) => {
      const ay = (a.bbox[1] + a.bbox[3]) / 2;
      const by = (b.bbox[1] + b.bbox[3]) / 2;
      if (Math.abs(ay - by) > 0.005) return ay - by;
      return a.bbox[0] - b.bbox[0];
    });

    // Group into lines by y-centre proximity (within 0.012 of each other)
    const LINE_THRESHOLD = 0.012;
    const lineGroups: any[][] = [];
    textUnitsRaw.forEach((unit: any) => {
      const cy = (unit.bbox[1] + unit.bbox[3]) / 2;
      const existing = lineGroups.find((grp) => {
        const grpCy = (grp[0].bbox[1] + grp[0].bbox[3]) / 2;
        return Math.abs(cy - grpCy) < LINE_THRESHOLD;
      });
      if (existing) {
        existing.push(unit);
      } else {
        lineGroups.push([unit]);
      }
    });

    // Build textLines: each legacy text_unit → its own single-unit cluster
    const textLines: any[] = lineGroups.map((units: any[], lineIdx: number) => {
      const clusters = units.map((unit: any, uIdx: number) => ({
        uuid: unit.uuid || `legacy-cluster-${lineIdx}-${uIdx}`,
        textUnits: [
          {
            uuid: unit.uuid || `legacy-unit-${lineIdx}-${uIdx}`,
            bbox: unit.bbox,
            text: unit.metadata.text,
            originalTextUnit: unit.uuid,
          },
        ],
        originalX: unit.bbox[0],
        alignedX: unit.bbox[0],
      }));
      return { uuid: `legacy-line-${lineIdx}`, clusters };
    });

    // Build molecules list
    // SMILES can live in many places depending on backend version.
    // Only accept string values — metadata.graph may be an object.
    const strOrEmpty = (v: any): string => (typeof v === "string" ? v : "");
    const extractSmiles = (o: any): string =>
      strOrEmpty(o.smiles) ||
      strOrEmpty(o.metadata?.smiles) ||
      strOrEmpty(o.metadata?.graph?.smiles) ||
      strOrEmpty(o.metadata?.graph?.transcription) ||
      strOrEmpty(o.metadata?.graph?.canonical_smiles) ||
      strOrEmpty(o.text) ||
      strOrEmpty(o.metadata?.text) ||
      "";

    const molecules: any[] = [
      ...moleculesRaw.map((o: any) => {
        const [bx1, by1, bx2, by2] = o.bbox;
        return {
          cls: o.cls,
          bbox: o.bbox,
          text: extractSmiles(o),
          uuid: o.uuid || "",
          // Store the raw OCSR graph so the frontend can call the render endpoint
          graph: normalizeGraph(o.metadata?.graph),
          // Pass aspect-ratio hint derived from the bbox for de-normalizing coords
          bbox_width: bx2 - bx1,
          bbox_height: by2 - by1,
        };
      }),
      ...tablesRaw.map((o: any) => ({
        cls: "table",
        bbox: o.bbox,
        text: "",
        uuid: o.uuid || "",
        data: o.metadata?.data || o.data || {},
      })),
    ];

    // Estimate a standard_height from the median text unit height
    const heights = textUnitsRaw.map((u: any) => u.bbox[3] - u.bbox[1]);
    heights.sort((a: number, b: number) => a - b);
    const medianHeight = heights[Math.floor(heights.length / 2)] || 0.02;

    console.log(
      "transformToPositionedFormat: legacy molecule smiles sample:",
      molecules
        .slice(0, 3)
        .map((m) => ({ cls: m.cls, text: m.text, uuid: m.uuid })),
    );
    console.log(
      "transformToPositionedFormat: legacy → textLines:",
      textLines.length,
      "molecules:",
      molecules.length,
    );

    return {
      textLines,
      molecules,
      rxns: [],
      corefs: [],
      reactionChains: [],
      metadata: {
        standard_height: medianHeight,
        standard_spacing: medianHeight * 0.3,
      },
    };
  }

  // If data has root property (new format), transform it
  if (data?.root && data?.objects) {
    const textLines: any[] = [];
    const molecules: any[] = [];
    const rxns: any[] = [];
    const corefs: any[] = [];
    let globalMetadata: any = null;

    // Build mapping from original text_unit UUID to text_unit_preview bbox
    // This is needed because corefs and conditions reference original text_unit UUIDs,
    // but the text has been repositioned in text_unit_preview objects
    const originalToPreviewBbox: Map<string, number[]> = new Map();

    // First pass: build the mapping by scanning all text_unit_preview objects
    Object.entries(data.objects).forEach(([, obj]: [string, any]) => {
      if (
        obj?.kind === "text_unit_preview" &&
        obj.metadata?.original_text_unit &&
        obj.bbox
      ) {
        originalToPreviewBbox.set(obj.metadata.original_text_unit, obj.bbox);
      }
    });

    // Iterate through root children to find text_line objects
    // root can be a UUID string (pipeline per-page format) or an object with children
    const rootNode =
      typeof data.root === "string" ? data.objects[data.root] : data.root;
    const rootChildren = rootNode?.children || [];

    // Check if structured text_lines exist — if so, skip raw text_unit nodes
    // (the backend keeps both original text_units and new text_lines in
    // root.children; rendering both causes duplicate offset text)
    const hasTextLines = rootChildren.some((childId: string) => {
      const node = data.objects[childId];
      return node?.kind === "text_line";
    });

    rootChildren.forEach((childId: string) => {
      const node = data.objects[childId];
      if (!node) return;

      // Handle text_line objects
      if (node.kind === "text_line") {
        const clusters: any[] = [];

        // Store global metadata from first line
        if (!globalMetadata && node.metadata) {
          globalMetadata = node.metadata;
        }

        // Iterate through cluster children
        (node.children || []).forEach((clusterId: string) => {
          const clusterNode = data.objects[clusterId];
          if (!clusterNode || clusterNode.kind !== "text_cluster") return;

          const textUnits: any[] = [];
          const clusterMetadata = clusterNode.metadata || {};

          // Iterate through text_unit_preview children in cluster
          (clusterNode.children || []).forEach((previewId: string) => {
            const previewNode = data.objects[previewId];
            if (!previewNode || previewNode.kind !== "text_unit_preview")
              return;

            const origUuid = previewNode.metadata?.original_text_unit;
            textUnits.push({
              uuid: previewId,
              bbox: previewNode.bbox,
              originalBbox: (origUuid && data.objects[origUuid]?.bbox) || previewNode.bbox,
              text: previewNode.text,
              parent: previewNode.parent,
              originalTextUnit: origUuid,
            });
          });

          if (textUnits.length > 0) {
            clusters.push({
              uuid: clusterId,
              textUnits,
              originalX:
                clusterMetadata.original_x || textUnits[0]?.bbox[0] || 0,
              alignedX:
                clusterMetadata.aligned_x ||
                clusterMetadata.original_x ||
                textUnits[0]?.bbox[0] ||
                0,
            });
          }
        });

        if (clusters.length > 0) {
          textLines.push({
            uuid: childId,
            clusters,
            blockKind: node.metadata?.block_kind,
          });
        }
      }

      // Handle text_unit nodes directly in root (only when structuring failed or was skipped)
      if (
        !hasTextLines &&
        node.kind === "text_unit" &&
        node.bbox &&
        node.text
      ) {
        textLines.push({
          uuid: childId,
          clusters: [
            {
              uuid: childId + "-cluster",
              textUnits: [
                {
                  uuid: childId,
                  bbox: node.bbox,
                  text: node.text,
                  parent: undefined,
                  originalTextUnit: childId,
                },
              ],
              originalX: node.bbox[0],
              alignedX: node.bbox[0],
            },
          ],
        });
      }

      // Handle molecules at root level. bbox is optional: manually-added
      // molecules carry only a SMILES and no positional info.
      if (node.kind === "molecule") {
        const bbox = node.bbox;
        const mol: any = {
          cls: "molecule",
          text: node.smiles || node.text || "",
          uuid: childId,
          graph: normalizeGraph(node.graph),
        };
        if (bbox) {
          const [bx1, by1, bx2, by2] = bbox;
          mol.bbox = bbox;
          mol.bbox_width = bx2 - bx1;
          mol.bbox_height = by2 - by1;
        }
        if (node.metadata?.source) mol.source = node.metadata.source;
        molecules.push(mol);
      }

      // Handle rxns at root level. bbox is optional: manually-added reactions
      // carry only SMILES references and no positional info.
      if (node.kind === "rxn") {
        // Helper to resolve reaction component - could be object with bbox or UUID string
        const resolveComponent = (item: any) => {
          if (typeof item === "string") {
            // It's a UUID - look up the object
            const referencedObj = data.objects[item];
            if (referencedObj) {
              const out: any = {
                category: "[Mol]",
                uuid: item,
                smiles: referencedObj.smiles || "",
                graph: normalizeGraph(referencedObj.graph),
              };
              if (referencedObj.bbox) {
                const [bx1, by1, bx2, by2] = referencedObj.bbox;
                out.bbox = referencedObj.bbox;
                out.bboxWidth = bx2 - bx1;
                out.bboxHeight = by2 - by1;
              }
              if (referencedObj.metadata?.source) {
                out.source = referencedObj.metadata.source;
              }
              return out;
            }
            return null;
          }
          // It's already an object with bbox - also resolve uuids for text
          if (item.uuids && Array.isArray(item.uuids)) {
            // Resolve text from text units and get preview bboxes
            const conditionTextUnitBboxes: { uuid: string; bbox: number[] }[] =
              [];
            const texts = item.uuids
              .map((uuid: string) => {
                const textUnit = data.objects[uuid];
                // Use the text_unit_preview bbox if available, otherwise fall back to original
                const previewBbox = originalToPreviewBbox.get(uuid);
                if (previewBbox) {
                  conditionTextUnitBboxes.push({ uuid, bbox: previewBbox });
                } else if (textUnit?.bbox) {
                  conditionTextUnitBboxes.push({ uuid, bbox: textUnit.bbox });
                }
                return textUnit?.text || "";
              })
              .filter(Boolean);
            item.resolvedText = texts.join(" ");
            item.resolvedTextUuids = item.uuids;
            item.conditionTextUnitBboxes = conditionTextUnitBboxes;
          }
          return item;
        };

        const rxnData: any = {
          cls: "rxn",
          text: node.text,
          uuid: childId,
          reactants: (node.reactants || [])
            .map(resolveComponent)
            .filter(Boolean),
          products: (node.products || []).map(resolveComponent).filter(Boolean),
          conditions: (node.conditions || [])
            .map(resolveComponent)
            .filter(Boolean),
        };
        if (node.bbox) rxnData.bbox = node.bbox;
        if (node.metadata?.source) rxnData.source = node.metadata.source;
        rxns.push(rxnData);
        molecules.push(rxnData);
      }

      // Handle moldetect_coref at root level
      if (node.kind === "moldetect_coref" && node.bbox) {
        // Resolve molecule UUID
        let moleculeData = node.molecule;
        if (typeof moleculeData === "string") {
          const mol = data.objects[moleculeData];
          if (mol) {
            moleculeData = {
              bbox: mol.bbox,
              uuid: moleculeData,
              smiles: mol.smiles || "",
            };
          }
        }

        // Resolve coref text UUIDs and collect individual text unit preview bboxes
        let corefData = node.coref;
        let corefText = "";
        let corefTextUuids: string[] = [];
        const corefTextUnitBboxes: { uuid: string; bbox: number[] }[] = [];

        if (Array.isArray(corefData)) {
          corefTextUuids = corefData;
          const texts = corefData
            .map((uuid: string) => {
              const textUnit = data.objects[uuid];
              // Use the text_unit_preview bbox if available, otherwise fall back to original
              const previewBbox = originalToPreviewBbox.get(uuid);
              if (previewBbox) {
                corefTextUnitBboxes.push({ uuid, bbox: previewBbox });
              } else if (textUnit?.bbox) {
                corefTextUnitBboxes.push({ uuid, bbox: textUnit.bbox });
              }
              return textUnit?.text || "";
            })
            .filter(Boolean);
          corefText = texts.join(" ");
        } else if (typeof corefData === "object" && corefData?.bbox) {
          // Still has bbox, not yet merged - use that bbox for text
          corefTextUnitBboxes.push({
            uuid: "coref-bbox",
            bbox: corefData.bbox,
          });
        }

        corefs.push({
          cls: "moldetect_coref",
          bbox: node.bbox,
          uuid: childId,
          molecule: moleculeData,
          coref: corefData,
          corefText,
          corefTextUuids,
          corefTextUnitBboxes, // Individual bboxes for each text unit (using preview positions)
        });
      }

      // Handle tables at root level
      if (node.kind === "table" && node.bbox) {
        molecules.push({
          cls: "table",
          bbox: node.bbox,
          text: node.text,
          uuid: childId,
          data: node.data || {},
        });
      }
    });

    // Extract text_block nodes (the green-bordered identified blocks). Each
    // block stores `metadata.source_unit_uuids` referencing the original
    // text_unit objects — gather their text in reading order (top→bottom,
    // left→right) for the card body.
    const objects: Record<string, any> = data.objects;
    const textBlocks: { uuid: string; bbox: number[]; blockKind?: string; text: string }[] = [];
    Object.entries(objects).forEach(([uuid, obj]: [string, any]) => {
      if (obj?.kind !== "text_block" || !obj.bbox) return;
      const sourceUuids: string[] = obj.metadata?.source_unit_uuids || [];
      const units = sourceUuids
        .map((u) => objects[u])
        .filter((n: any) => n && typeof n.text === "string" && n.text.trim() && Array.isArray(n.bbox));
      units.sort((a: any, b: any) => {
        const ay = (a.bbox[1] + a.bbox[3]) / 2;
        const by = (b.bbox[1] + b.bbox[3]) / 2;
        const avgH = Math.max(a.bbox[3] - a.bbox[1], b.bbox[3] - b.bbox[1]);
        if (Math.abs(ay - by) > avgH * 0.5) return ay - by;
        return a.bbox[0] - b.bbox[0];
      });
      const text = units.map((n: any) => n.text.trim()).filter(Boolean).join(" ").trim();
      textBlocks.push({
        uuid,
        bbox: obj.bbox,
        ...(obj.metadata?.block_kind && { blockKind: obj.metadata.block_kind }),
        text,
      });
    });

    // Build reaction chains
    const reactionChains = buildReactionChains(rxns, molecules, corefs);

    console.log(
      "transformToPositionedFormat: Extracted",
      textLines.length,
      "text lines,",
      molecules.length,
      "molecules/rxns,",
      corefs.length,
      "corefs,",
      reactionChains.length,
      "reaction chains",
    );
    console.log(
      "transformToPositionedFormat: Chains:",
      reactionChains.map((c) => ({
        id: c.id,
        components: c.allComponents?.length,
        color: c.color,
      })),
    );
    return {
      textLines,
      textBlocks,
      molecules,
      rxns,
      corefs,
      reactionChains,
      metadata: globalMetadata,
    };
  }

  // Return original data if format is unrecognized
  console.log("transformToPositionedFormat: Unrecognized format", data);
  return data;
};

// Color palette for reaction chains
const CHAIN_COLORS = [
  "rgba(59, 130, 246, 0.25)", // blue
  "rgba(16, 185, 129, 0.25)", // green
  "rgba(245, 158, 11, 0.25)", // amber
  "rgba(239, 68, 68, 0.25)", // red
  "rgba(139, 92, 246, 0.25)", // purple
  "rgba(236, 72, 153, 0.25)", // pink
  "rgba(6, 182, 212, 0.25)", // cyan
  "rgba(132, 204, 22, 0.25)", // lime
];

// Build reaction chains by matching products to reactants via IoU
function buildReactionChains(
  rxns: any[],
  molecules: any[],
  corefs: any[],
): any[] {
  if (rxns.length === 0) {
    // No reactions - create chains for standalone molecules with corefs
    const chains: any[] = [];
    const moleculesInCorefs = new Set<string>();

    corefs.forEach((coref, idx) => {
      if (coref.molecule?.uuid) {
        moleculesInCorefs.add(coref.molecule.uuid);
        chains.push({
          id: `standalone-${idx}`,
          color: CHAIN_COLORS[idx % CHAIN_COLORS.length],
          rxns: [],
          molecules: [coref.molecule.uuid],
          corefs: [coref],
          conditionTexts: [],
          allComponents: [
            {
              type: "molecule",
              uuid: coref.molecule.uuid,
              bbox: coref.molecule.bbox,
            },
            { type: "coref", data: coref },
          ],
        });
      }
    });

    // Add molecules not in any coref
    const standaloneStart = chains.length;
    molecules.forEach((mol, idx) => {
      if (mol.cls === "molecule" && !moleculesInCorefs.has(mol.uuid)) {
        chains.push({
          id: `molecule-${idx}`,
          color: CHAIN_COLORS[(standaloneStart + idx) % CHAIN_COLORS.length],
          rxns: [],
          molecules: [mol.uuid],
          corefs: [],
          conditionTexts: [],
          allComponents: [{ type: "molecule", uuid: mol.uuid, bbox: mol.bbox }],
        });
      }
    });

    return chains;
  }

  // Build graph: find which reactions are connected
  // Two reactions are connected if a product of one overlaps significantly with a reactant of another

  // Helper to compute IoU
  const computeIoU = (
    bbox1: number[] | undefined,
    bbox2: number[] | undefined,
  ): number => {
    if (!bbox1 || !bbox2 || bbox1.length !== 4 || bbox2.length !== 4) return 0;

    const b1x1 = bbox1[0]!;
    const b1y1 = bbox1[1]!;
    const b1x2 = bbox1[2]!;
    const b1y2 = bbox1[3]!;
    const b2x1 = bbox2[0]!;
    const b2y1 = bbox2[1]!;
    const b2x2 = bbox2[2]!;
    const b2y2 = bbox2[3]!;

    const x1 = Math.max(b1x1, b2x1);
    const y1 = Math.max(b1y1, b2y1);
    const x2 = Math.min(b1x2, b2x2);
    const y2 = Math.min(b1y2, b2y2);

    if (x2 <= x1 || y2 <= y1) return 0;

    const intersection = (x2 - x1) * (y2 - y1);
    const area1 = (b1x2 - b1x1) * (b1y2 - b1y1);
    const area2 = (b2x2 - b2x1) * (b2y2 - b2y1);
    const union = area1 + area2 - intersection;

    return union > 0 ? intersection / union : 0;
  };

  // Build adjacency list for reactions
  const adjacency: Map<number, Set<number>> = new Map();
  rxns.forEach((_, idx) => adjacency.set(idx, new Set()));

  // Check each pair of reactions
  for (let i = 0; i < rxns.length; i++) {
    for (let j = i + 1; j < rxns.length; j++) {
      // Check if any product of i matches any reactant of j (or vice versa)
      let connected = false;

      // Products of i vs reactants of j
      for (const product of rxns[i].products || []) {
        for (const reactant of rxns[j].reactants || []) {
          if (product.uuid && reactant.uuid && product.uuid === reactant.uuid) {
            connected = true;
            break;
          }
          if (
            product.bbox &&
            reactant.bbox &&
            computeIoU(product.bbox, reactant.bbox) > 0.5
          ) {
            connected = true;
            break;
          }
        }
        if (connected) break;
      }

      // Products of j vs reactants of i
      if (!connected) {
        for (const product of rxns[j].products || []) {
          for (const reactant of rxns[i].reactants || []) {
            if (
              product.uuid &&
              reactant.uuid &&
              product.uuid === reactant.uuid
            ) {
              connected = true;
              break;
            }
            if (
              product.bbox &&
              reactant.bbox &&
              computeIoU(product.bbox, reactant.bbox) > 0.5
            ) {
              connected = true;
              break;
            }
          }
          if (connected) break;
        }
      }

      if (connected) {
        adjacency.get(i)!.add(j);
        adjacency.get(j)!.add(i);
      }
    }
  }

  // Find connected components using BFS
  const visited = new Set<number>();
  const components: number[][] = [];

  for (let i = 0; i < rxns.length; i++) {
    if (visited.has(i)) continue;

    const component: number[] = [];
    const queue = [i];

    while (queue.length > 0) {
      const curr = queue.shift()!;
      if (visited.has(curr)) continue;
      visited.add(curr);
      component.push(curr);

      adjacency.get(curr)!.forEach((neighbor) => {
        if (!visited.has(neighbor)) {
          queue.push(neighbor);
        }
      });
    }

    components.push(component);
  }

  // Build chain objects
  const chains: any[] = components.map((component, idx) => {
    const chainRxns = component.map((i) => rxns[i]);
    const chainMoleculeUuids = new Set<string>();
    const addedMoleculeUuids = new Set<string>(); // Track which molecules have been added to avoid duplicates
    const chainConditionTexts: any[] = [];
    const allComponents: any[] = [];

    // Collect all molecules and conditions from reactions in this chain
    // Deduplicate molecules that appear as both reactant and product in multi-step reactions
    chainRxns.forEach((rxn) => {
      allComponents.push({ type: "rxn", uuid: rxn.uuid, bbox: rxn.bbox });

      (rxn.reactants || []).forEach((r: any) => {
        if (r.uuid) {
          chainMoleculeUuids.add(r.uuid);
          // Only add this molecule component if we haven't added it yet
          if (!addedMoleculeUuids.has(r.uuid)) {
            addedMoleculeUuids.add(r.uuid);
            allComponents.push({
              type: "reactant",
              data: r,
              bbox: r.bbox,
              category: r.category,
            });
          }
        } else {
          // No UUID means it's not a molecule reference, add it anyway
          allComponents.push({
            type: "reactant",
            data: r,
            bbox: r.bbox,
            category: r.category,
          });
        }
      });
      (rxn.products || []).forEach((p: any) => {
        if (p.uuid) {
          chainMoleculeUuids.add(p.uuid);
          // Only add this molecule component if we haven't added it yet
          if (!addedMoleculeUuids.has(p.uuid)) {
            addedMoleculeUuids.add(p.uuid);
            allComponents.push({
              type: "product",
              data: p,
              bbox: p.bbox,
              category: p.category,
            });
          }
        } else {
          allComponents.push({
            type: "product",
            data: p,
            bbox: p.bbox,
            category: p.category,
          });
        }
      });
      (rxn.conditions || []).forEach((c: any) => {
        allComponents.push({
          type: "condition",
          data: c,
          bbox: c.bbox,
          category: c.category,
        });
        if (c.resolvedText) {
          chainConditionTexts.push({
            text: c.resolvedText,
            textUuids: c.resolvedTextUuids || [],
            bbox: c.bbox,
          });
        }
      });
    });

    // Find corefs that reference molecules in this chain
    const chainCorefs = corefs.filter((coref) => {
      const molUuid =
        typeof coref.molecule === "string"
          ? coref.molecule
          : coref.molecule?.uuid;
      return molUuid && chainMoleculeUuids.has(molUuid);
    });

    chainCorefs.forEach((coref) => {
      allComponents.push({ type: "coref", data: coref });
    });

    return {
      id: `chain-${idx}`,
      color: CHAIN_COLORS[idx % CHAIN_COLORS.length],
      rxns: chainRxns,
      molecules: Array.from(chainMoleculeUuids),
      corefs: chainCorefs,
      conditionTexts: chainConditionTexts,
      allComponents,
    };
  });

  // Handle molecules not in any reaction chain
  const moleculesInChains = new Set<string>();
  chains.forEach((chain) => {
    chain.molecules.forEach((uuid: string) => moleculesInChains.add(uuid));
  });

  // Find corefs for standalone molecules
  const standaloneMolecules = molecules.filter(
    (mol) => mol.cls === "molecule" && !moleculesInChains.has(mol.uuid),
  );

  const usedCorefs = new Set<string>();
  chains.forEach((chain) => {
    chain.corefs.forEach((c: any) => usedCorefs.add(c.uuid));
  });

  standaloneMolecules.forEach((mol, idx) => {
    const molCorefs = corefs.filter((coref) => {
      if (usedCorefs.has(coref.uuid)) return false;
      const molUuid =
        typeof coref.molecule === "string"
          ? coref.molecule
          : coref.molecule?.uuid;
      return molUuid === mol.uuid;
    });

    molCorefs.forEach((c) => usedCorefs.add(c.uuid));

    const allComponents: any[] = [
      { type: "molecule", uuid: mol.uuid, bbox: mol.bbox },
    ];
    molCorefs.forEach((coref) => {
      allComponents.push({ type: "coref", data: coref });
    });

    chains.push({
      id: `standalone-${idx}`,
      color: CHAIN_COLORS[(components.length + idx) % CHAIN_COLORS.length],
      rxns: [],
      molecules: [mol.uuid],
      corefs: molCorefs,
      conditionTexts: [],
      allComponents,
    });
  });

  return chains;
}

type TextBlock = { uuid: string; bbox: number[]; text: string; blockKind?: string };

// Spatial fallback: find short text just below a molecule bbox that looks like a label/yield.
export const inferMetadataFromContext = (
  moleculeBbox: number[] | undefined,
  textLines: any[],
  textBlocks: TextBlock[],
): string | null => {
  if (!moleculeBbox || moleculeBbox.length < 4) return null;
  const mx1 = moleculeBbox[0]!;
  const mx2 = moleculeBbox[2]!;
  const my2 = moleculeBbox[3]!;
  const mw = mx2 - mx1;
  const mh = moleculeBbox[3]! - moleculeBbox[1]!;
  if (mw <= 0 || mh <= 0) return null;

  type Cand = { text: string; bbox: number[]; blockKind?: string };
  const candidates: Cand[] = [];

  (textBlocks || []).forEach((block) => {
    const text = (block.text || "").trim();
    if (!text || !block.bbox || block.bbox.length < 4) return;
    const cand: Cand = { text, bbox: block.bbox };
    if (block.blockKind) cand.blockKind = block.blockKind;
    candidates.push(cand);
  });

  (textLines || []).forEach((line: any) => {
    const units: { text: string; bbox: number[] }[] = [];
    (line.clusters || []).forEach((cluster: any) => {
      (cluster.textUnits || []).forEach((unit: any) => {
        const bbox = unit.originalBbox || unit.bbox;
        const text = (unit.text || "").trim();
        if (bbox && bbox.length >= 4 && text) units.push({ bbox, text });
      });
    });
    if (units.length === 0) return;
    units.sort((a, b) => {
      const ay = (a.bbox[1]! + a.bbox[3]!) / 2;
      const by = (b.bbox[1]! + b.bbox[3]!) / 2;
      if (Math.abs(ay - by) > 0.005) return ay - by;
      return a.bbox[0]! - b.bbox[0]!;
    });
    const text = units.map((u) => u.text).join(" ").trim();
    if (!text) return;
    const bbox = [
      Math.min(...units.map((u) => u.bbox[0]!)),
      Math.min(...units.map((u) => u.bbox[1]!)),
      Math.max(...units.map((u) => u.bbox[2]!)),
      Math.max(...units.map((u) => u.bbox[3]!)),
    ];
    candidates.push({ text, bbox, blockKind: line.blockKind });
  });

  const isProse = (k?: string) => k === "paragraph" || k === "sentence" || k === "header";
  const nonProse = candidates.filter((c) => !isProse(c.blockKind));

  const below: (Cand & { dy: number })[] = [];
  for (const c of nonProse) {
    const cx1 = c.bbox[0]!;
    const cy1 = c.bbox[1]!;
    const cx2 = c.bbox[2]!;
    if (cy1 < my2 - mh * 0.1) continue;
    const dy = cy1 - my2;
    if (dy > mh * 2) continue;
    const overlap = Math.max(0, Math.min(mx2, cx2) - Math.max(mx1, cx1));
    if (overlap < mw * 0.3) continue;
    below.push({ ...c, dy });
  }

  if (below.length === 0) return null;
  below.sort((a, b) => a.dy - b.dy);

  const extendCaption = (chosen: Cand): string => {
    let text = chosen.text;
    let left = chosen.bbox[0]!;
    let right = chosen.bbox[2]!;
    let bottom = chosen.bbox[3]!;
    const seenBboxKeys = new Set<string>([chosen.bbox.join(",")]);
    for (let i = 0; i < 4; i++) {
      let pick: Cand | null = null;
      let pickDy = Infinity;
      for (const c of nonProse) {
        const key = c.bbox.join(",");
        if (seenBboxKeys.has(key)) continue;
        if (text.includes(c.text)) continue;
        const cy1 = c.bbox[1]!;
        const cy2 = c.bbox[3]!;
        const lineH = cy2 - cy1;
        const dy = cy1 - bottom;
        if (dy < -lineH * 0.3) continue;
        if (dy > lineH * 1.2) continue;
        const overlap = Math.max(0, Math.min(right, c.bbox[2]!) - Math.max(left, c.bbox[0]!));
        const width = right - left;
        if (overlap < width * 0.3) continue;
        if (dy < pickDy) { pick = c; pickDy = dy; }
      }
      if (!pick) break;
      text += " " + pick.text;
      left = Math.min(left, pick.bbox[0]!);
      right = Math.max(right, pick.bbox[2]!);
      bottom = Math.max(bottom, pick.bbox[3]!);
      seenBboxKeys.add(pick.bbox.join(","));
    }
    return text;
  };

  const yieldRe = /\d+\.?\d*\s*%/;
  const labelRe = /^\d+[a-zA-Z]?$/;
  const singleLetterRe = /^[a-zA-Z]$/;
  const schemeRe = /^Scheme\s+\d+/i;

  for (const c of below) if (yieldRe.test(c.text)) return extendCaption(c);
  for (const c of below) if (labelRe.test(c.text) || singleLetterRe.test(c.text)) return extendCaption(c);
  for (const c of below) if (schemeRe.test(c.text)) return extendCaption(c);
  for (const c of below) {
    if (c.text.length <= 30 && /^[a-zA-Z][a-zA-Z0-9-]{2,29}$/.test(c.text)) return extendCaption(c);
  }

  return null;
};

// Transform page data into a clean digital format — only human-readable extracted
// content. rawData is the authoritative source for all three content types.
export const transformToDigitalFormat = (readableData: any, rawData?: any): {
  text: string[];
  molecules: (string | { smiles: string; metadata: string })[];
  reactions: { smiles: string; conditions: string[] }[];
  tables: { json: any; markdown: string | null }[];
} => {
  const textArr: string[] = [];
  const molecules: (string | { smiles: string; metadata: string })[] = [];
  const reactions: { smiles: string; conditions: string[] }[] = [];
  const tables: { json: any; markdown: string | null }[] = [];

  const data = rawData ?? readableData;
  if (!data) return { text: textArr, molecules, reactions, tables };

  const extractSmiles = (node: any): string => {
    const candidates = [
      node.smiles,
      node.graph?.smiles,
      node.graph?.canonical_smiles,
      node.graph?.transcription,
      node.text,
      node.metadata?.smiles,
      node.metadata?.graph?.smiles,
      node.metadata?.graph?.canonical_smiles,
      node.metadata?.graph?.transcription,
      node.metadata?.text,
    ];
    for (const c of candidates) {
      if (c && typeof c === "string" && c.trim()) return c.trim();
    }
    return "";
  };

  // New format: root + objects dict
  if (data.root && data.objects && !Array.isArray(data.objects)) {
    const objects: Record<string, any> = data.objects;

    // Collect text_blocks sorted by spatial position (top-to-bottom, left-to-right)
    const textBlockEntries = Object.entries(objects)
      .filter(([, node]: [string, any]) => node?.kind === "text_block" && node?.bbox)
      .sort(([, a]: [string, any], [, b]: [string, any]) => {
        const ay = (a.bbox[1] + a.bbox[3]) / 2;
        const by = (b.bbox[1] + b.bbox[3]) / 2;
        const avgH = Math.max(a.bbox[3] - a.bbox[1], b.bbox[3] - b.bbox[1]);
        if (Math.abs(ay - by) > avgH * 0.5) return ay - by;
        return a.bbox[0] - b.bbox[0];
      });

    textBlockEntries.forEach(([, node]: [string, any]) => {
      const sourceUuids: string[] = node.metadata?.source_unit_uuids || [];
      const units = sourceUuids
        .map((u: string) => objects[u])
        .filter((n: any) => n && typeof n.text === "string" && n.text.trim() && Array.isArray(n.bbox));
      units.sort((a: any, b: any) => {
        const ay = (a.bbox[1] + a.bbox[3]) / 2;
        const by = (b.bbox[1] + b.bbox[3]) / 2;
        const avgH = Math.max(a.bbox[3] - a.bbox[1], b.bbox[3] - b.bbox[1]);
        if (Math.abs(ay - by) > avgH * 0.5) return ay - by;
        return a.bbox[0] - b.bbox[0];
      });
      const blockText = units.map((n: any) => n.text.trim()).filter(Boolean).join(" ").trim();
      if (blockText) textArr.push(blockText);
    });

    // Build molecule UUID → label map using positioned format (explicit corefs + spatial inference)
    const molLabelMap = new Map<string, string>();
    const positioned = transformToPositionedFormat(readableData);
    if (positioned) {
      (positioned.corefs || []).forEach((coref: any) => {
        const molUuid = typeof coref.molecule === "string" ? coref.molecule : coref.molecule?.uuid;
        if (molUuid && coref.corefText) molLabelMap.set(molUuid, coref.corefText);
      });
      (positioned.molecules || []).forEach((mol: any) => {
        if (!mol.uuid || molLabelMap.has(mol.uuid)) return;
        const label = inferMetadataFromContext(mol.bbox, positioned.textLines || [], positioned.textBlocks || []);
        if (label) molLabelMap.set(mol.uuid, label);
      });
    }

    Object.entries(objects).forEach(([uuid, node]: [string, any]) => {
      if (!node?.kind) return;

      if (node.kind === "molecule") {
        const smiles = extractSmiles(node);
        if (!smiles) return;
        const label = molLabelMap.get(uuid);
        molecules.push(label ? { smiles, metadata: label } : smiles);
      }

      if (node.kind === "rxn") {
        const reactantSmiles = (node.reactants || [])
          .map((ref: any) => {
            const mol = typeof ref === "string" ? objects[ref] : ref;
            return mol ? extractSmiles(mol) : "";
          })
          .filter(Boolean);
        const productSmiles = (node.products || [])
          .map((ref: any) => {
            const mol = typeof ref === "string" ? objects[ref] : ref;
            return mol ? extractSmiles(mol) : "";
          })
          .filter(Boolean);
        const conditionTexts = (node.conditions || [])
          .map((cond: any) => {
            if (cond.uuids && Array.isArray(cond.uuids)) {
              return cond.uuids.map((u: string) => objects[u]?.text || "").filter(Boolean).join(" ");
            }
            return "";
          })
          .filter(Boolean);
        const smiles = `${reactantSmiles.join(".")}>>${productSmiles.join(".")}`;
        reactions.push({ smiles, conditions: conditionTexts });
      }

      if (node.kind === "table") {
        const json = node.data?.json;
        const markdown = node.data?.markdown;
        if (json || markdown) tables.push({ json: json ?? null, markdown: markdown ?? null });
      }
    });

    return { text: textArr, molecules, reactions, tables };
  }

  // Legacy format: { objects: [...] }
  if (data.objects && Array.isArray(data.objects)) {
    // Build molecule UUID → coref label map for legacy format
    const legacyMolCorefLabel = new Map<string, string>();
    (data.objects as any[])
      .filter((o: any) => o.cls === "moldetect_coref")
      .forEach((o: any) => {
        const molUuid = typeof o.molecule === "string" ? o.molecule : o.molecule?.uuid;
        if (!molUuid) return;
        const corefText = typeof o.coref === "string" ? o.coref : (o.corefText ?? "");
        if (corefText) legacyMolCorefLabel.set(molUuid, corefText);
      });

    (data.objects as any[])
      .filter((o: any) => o.cls === "molecule")
      .forEach((o: any) => {
        const smiles = extractSmiles(o);
        if (!smiles) return;
        const label = legacyMolCorefLabel.get(o.uuid);
        molecules.push(label ? { smiles, metadata: label } : smiles);
      });
    (data.objects as any[])
      .filter((o: any) => o.cls === "table")
      .forEach((o: any) => {
        const tableData = o.metadata?.data || o.data || {};
        const json = tableData.json;
        const markdown = tableData.markdown;
        if (json || markdown) tables.push({ json: json ?? null, markdown: markdown ?? null });
      });
  }

  return { text: textArr, molecules, reactions, tables };
};

// Build mapping from original text_unit UUID to its text_unit_preview UUIDs
export const buildHoverMapping = (data: any): Map<string, string[]> => {
  const mapping = new Map<string, string[]>();

  if (!data?.root || !data?.objects) {
    return mapping;
  }

  // Find all text_unit_preview objects and map them by their original_text_unit
  Object.keys(data.objects).forEach((key) => {
    const node = data.objects[key];
    if (
      node?.kind === "text_unit_preview" &&
      node.metadata?.original_text_unit
    ) {
      const originalTextUnit = node.metadata.original_text_unit;
      if (!mapping.has(originalTextUnit)) {
        mapping.set(originalTextUnit, []);
      }
      mapping.get(originalTextUnit)!.push(key);
    }
  });

  return mapping;
};
